import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { Playlist } from "../api/types";

/**
 * "Add to playlist…": my lists, most recently changed first, with a way to
 * make a new one at the bottom. Picking one posts and closes.
 *
 * The picker is where a first playlist gets made, so there is no empty page
 * to visit first: the moment somebody wants a track kept, they can keep it.
 *
 * `what` is one track ({ release, pos }) or a whole release ({ release }):
 * the API appends every playable track of a release given no position.
 */
export function PlaylistPicker({ what, at, onDone, onClose }: {
  what: { release: string; pos?: number; label: string };
  /** Where it opens, in viewport pixels. */
  at: { x: number; y: number };
  onDone: (msg: string) => void;
  onClose: () => void;
}) {
  const [lists, setLists] = useState<Playlist[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    api.playlists()
      .then((p) => alive && setLists(p))
      .catch((e: ApiError) => alive && setError(e.message));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Capture-phase, and the target is checked first, for the reason the
    // track menu gives: a dismiss that beats the picker's own buttons would
    // unmount them before they ran.
    const close = (e: Event) => {
      if (box.current?.contains(e.target as Node)) return;
      onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  async function add(id: number, listName: string) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.addToPlaylist(id, what.release, what.pos);
      onDone(r.added === 0
        ? `already on ${listName}`
        : `added ${r.added === 1 ? what.label : `${r.added} tracks`} to ${listName}`);
    } catch (e) {
      setError((e as ApiError).message);
      setBusy(false);
    }
  }

  async function create() {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.createPlaylist({ name: n });
      await add(id, n);
    } catch (e) {
      setError((e as ApiError).message);
      setBusy(false);
    }
  }

  // Keep it on screen: open upwards near the bottom, leftwards near the edge.
  // On a phone it is a sheet along the bottom instead (styles.css), and the
  // keyboard is not raised until asked: a keyboard that opens under a popup
  // pinned to a tap is a popup nobody can see.
  const phone = window.matchMedia("(max-width: 640px)").matches;
  const style: React.CSSProperties | undefined = phone ? undefined : {
    left: Math.max(8, Math.min(at.x, window.innerWidth - 290)),
    top: at.y + 300 > window.innerHeight ? undefined : at.y,
    bottom: at.y + 300 > window.innerHeight ? window.innerHeight - at.y : undefined,
  };

  return (
    <div className="picker" ref={box} style={style} role="menu">
      <div className="pk-hd">
        Add <b title={what.label}>{what.label}</b> to…
      </div>
      {error && <div className="pk-err">{error}</div>}
      {!lists && !error && <div className="pk-note">loading your playlists…</div>}
      {lists && lists.length === 0 && (
        <div className="pk-note">No playlists yet. Name one below.</div>
      )}
      {lists && lists.length > 0 && (
        <ul>
          {lists.map((p) => (
            <li key={p.id}>
              <button disabled={busy} onClick={() => void add(p.id, p.name)}>
                <span className="nm">{p.name}</span>
                <span className="n">{p.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="pk-new" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <input
          autoFocus={lists?.length === 0 && !phone}
          placeholder="New playlist…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          disabled={busy}
        />
        <button type="submit" disabled={busy || !name.trim()}>Make</button>
      </form>
    </div>
  );
}

/**
 * A line that fades: "added X to Y". One at a time, above everything, gone
 * in a few seconds. Kept here because the picker is what produces them.
 */
export function Toast({ msg, onGone }: { msg: string | null; onGone: () => void }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onGone, 3200);
    return () => clearTimeout(t);
  }, [msg, onGone]);
  if (!msg) return null;
  return <div className="toast" role="status">{msg}</div>;
}
